import { Component, HostBinding, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { DatePipe, Location, NgClass } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { NotificationsService } from 'angular2-notifications';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipDirective, TooltipModule } from 'ngx-bootstrap/tooltip';
import { Subject, lastValueFrom } from 'rxjs';
import { takeUntil, finalize, first, share, take } from 'rxjs/operators';
import { Range } from 'quill/core/selection';

import { CategorySelectorComponent } from './category-selector/category-selector.component';
import { CompleteTicketModalComponent } from '../shared/modal-windows/complete-ticket-modal/complete-ticket-modal.component';
import { AttachmentsModalComponent } from '../shared/modal-windows/attachments-modal/attachments-modal.component';
import { ConfirmationModalComponent } from '../shared/modal-windows/confirmation-modal/confirmation-modal.component';
import { AddTagsModalComponent } from '@form-field-module/modal-windows/add-tags-modal/add-tags-modal.component';
import { SuperOfficeIntegrationModalComponent } from '../shared/modal-windows/super-office-integration-modal/super-office-integration-modal.component';
import { AddPrivateReplyModalComponent } from '../shared/modal-windows/add-private-reply-modal/add-private-reply-modal.component';
import { CloseTicketWithUnhandledRepliesModalComponent } from '../shared/modal-windows/close-ticket-with-unhandled-replies-modal/close-ticket-with-unhandled-replies-modal.component';
import { RecipientConfirmationModalComponent } from '../shared/modal-windows/recipient-confirmation-modal/recipient-confirmation-modal.component';
import { StickyHeaderDirective } from '../shared/directives/sticky-header.directive';
import { TicketService } from '../core/services/ticket/ticket.service';
import { ComponentMessengerService } from '../core/services/component-messenger.service';
import { RightPanelService } from '../core/services/right-panel.service';
import { ReplyStoreService } from '../core/services/ticket/reply-store.service';
import { ReplyDataService } from '../core/services/ticket/reply-data.service';
import { SignalRService } from '../core/services/signalR.service';
import { TicketDetailsPanelService } from '../core/services/ticket-details-panel.service';
import { NoteService } from '../core/services/note.service';
import { TicketRightPanelService } from '../core/services/ticket-right-panel.service';
import { ReplyService } from '../core/services/ticket/reply.service';
import { SuperOfficeService } from '../core/services/super-office.service';
import { AssignService } from '../core/services/ticket/assign.service';
import { TagService } from '@sb-tools/typescript/services/tag.service';
import { FieldsService } from '../core/services/ticket/fields.service';
import { ReplySortingService } from '../core/services/ticket/reply-sorting.service';
import { ThreadFilterService } from '../core/services/thread-filter.service';
import { OpenTicketInNewWindowService } from '../core/services/ticket/open-ticket-in-new-window.service';
import { FieldsLogService } from '../core/services/fields-log.service';
import { Tag } from '@sb-tools/typescript/models/tag.model';
import { TicketTabType } from '../core/enums/ticket-tab-type.enum';
import { RightPanelType } from '../core/enums/right-panel-type.enum';
import { TicketView } from '../core/enums/ticket-view.enum';
import { ReplyMentionsToCodePipe } from '../shared/pipes/reply-mentions-to-code.pipe';

import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { HelperService } from '@sb-tools/typescript/services/helper.service';
import { ReplyTemplateManagerService } from '@sb-tools/typescript/services/reply-template-manager.service';
import { QuillHelperService } from '@sb-tools/typescript/services/quill/quill-helper.service';
import { SignatureService } from '@sb-tools/typescript/services/signature.service';
import { UserService } from '@sb-tools/typescript/services/user.service';
import { SmilesService } from '@sb-tools/typescript/services/smiles.service';
import { MessageDTO } from '@sb-tools/typescript/models/dto/message.dto';
import { UserWithLabelModel } from '@sb-tools/typescript/models/user-with-label.model';
import { BaseUserWithRole } from '@sb-tools/typescript/models/base-user-with-role.model';
import { InboxAttachmentBase } from '@sb-tools/typescript/models/inbox-attachment-base.model';
import { ReplyUpdateModel } from '@sb-tools/typescript/models/reply-update-model.model';
import { ThreadElementDTO } from '@sb-tools/typescript/models/dto/thread-element.dto';
import { TicketSource } from '@sb-tools/typescript/enums/ticket-source.enum';
import { MessagesOrder } from 'src/sb-tools/typescript/enums/messages-order.enum';
import { DetailedTicketSource } from '@sb-tools/typescript/enums/detailed-ticket-source.enum';
import { LocalizationLanguage } from '@sb-tools/typescript/enums/localization-language.enum';
import { TicketStatus } from '@sb-tools/typescript/enums/ticket-status.enum';
import { UserRole } from '@sb-tools/typescript/enums/user-role.enum';
import { SessionAuthenticationLevel } from '@sb-tools/typescript/enums/session-authentication-level.enum';
import { MessageThreadElementType } from '@sb-tools/typescript/enums/message-thread-element-type.enum';
import { NewLinePipe } from '@sb-tools/typescript/pipes/new-line.pipe';
import { KEY_A, KEY_C, KEY_L, KEY_N, KEY_R, KEY_S, TAB } from '@sb-tools/typescript/constants/keycodes';

import { ReplyTemplatesModalComponent } from 'src/sb-modules/Frontend-Reply-Templates-Module/reply-templates-modal.component';
import { Sentiments } from 'src/sb-modules/Frontend-form-field-module/common/enums/sentiments.enum';
import { AttachmentBase } from '@sb-tools/typescript/models/attachment-base.model';
import { DetailedTicketDTO } from '../core/models/dto/detailed-ticket.dto';
import { SatisfactionDTO } from '../core/models/dto/satisfaction.dto';
import { SurveyDTO } from '../core/models/dto/survey.dto';
import { SendTicketToSORequest } from '../core/models/send-ticket-to-so-request.model';
import { ReplySuggestionsUpdatedEventModel } from '../core/models/dto/signalR/reply-suggestions-updated-event-model.model';
import { ReplyAddedEventModel } from '../core/models/dto/signalR/reply-added-event-model.model';
import { ReplyDeletedEventModel } from '../core/models/dto/signalR/reply-deleted-event-model.model';
import { ReplyUpdatedEventModel } from '../core/models/dto/signalR/reply-updated-event-model.model';
import { TicketSignalRDTO } from '../core/models/dto/signalR/ticket-signalr.dto';
import { NoteCountUpdatedEventModel } from '../core/models/dto/signalR/note-count-updated-event-model.model';
import { TicketThreadUpdatedEventModel } from '../core/models/dto/signalR/ticket-thread-updated-event-model.model';
import { TicketAssigneeUpdatedEventModel } from '../core/models/dto/signalR/ticket-assignee-updated-event-model.model';
import { TicketPropertiesUpdatedEventModel } from '../core/models/dto/signalR/ticket-properties-updated-event-model.model';
import { TicketUpdatedEventModel } from '../core/models/dto/signalR/ticket-updated-event-model.model';
import { CommunityUserExtended } from '@sb-tools/typescript/models/community-user-extended.model';
import { ImportanceComponent } from './importance/importance.component';
import { ClipboardModule } from 'ngx-clipboard';
import { InlineSVGComponent } from '../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { TicketTitleComponent } from './ticket-title/ticket-title.component';
import { FormsModule } from '@angular/forms';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { TagsComponent } from './tags/tags.component';
import { AssignBlockComponent } from '../shared/components/assign-block/assign-block.component';
import { TicketToolbarComponent } from './ticket-toolbar/ticket-toolbar.component';
import { ThreadFilterComponent } from './thread/ticket-thread-social-media/thread-filter/thread-filter.component';
import { TicketAlertsComponent } from './ticket-alerts/ticket-alerts.component';
import { LoaderComponent } from '@sb-tools/typescript/components/loader/loader.component';
import { AddReplyNoteComponent } from './add-reply-note/add-reply-note.component';
import { TicketThreadWebComponent } from './thread/ticket-thread-web/ticket-thread-web.component';
import { TicketThreadChatComponent } from './thread/ticket-thread-chat/ticket-thread-chat.component';
import { TicketThreadSocialMediaComponent } from './thread/ticket-thread-social-media/ticket-thread-social-media.component';
import { TicketNotesComponent } from './ticket-right-panel/ticket-notes/ticket-notes.component';
import { RepliesBulkOperationMenuComponent } from './replies-bulk-operation-menu/replies-bulk-operation-menu.component';
import { TicketRightPanelComponent } from './ticket-right-panel/ticket-right-panel.component';
import { EnumToArrayPipe } from '@sb-tools/typescript/pipes/enum-to-array.pipe';
import { DetailedTicketSourcePipe } from '../shared/pipes/detailed-ticket-source.pipe';
import { SentimentPipe } from '../shared/pipes/sentiment.pipe';
import { InboxElementsVisibility } from '@sb-tools/typescript/models/inbox-elements-visibility.model';
import { isInboxElementVisible } from '../core/utils/is-inbox-element-visible.util';

@Component({
    selector: 'app-ticket-details',
    templateUrl: './ticket-details.component.html',
    styleUrls: ['./ticket-details.component.scss'],
    imports: [
        NgClass,
        DatePipe,
        ImportanceComponent,
        RouterLink,
        TooltipModule,
        ClipboardModule,
        InlineSVGComponent,
        TicketTitleComponent,
        FormsModule,
        NgSelectComponent,
        NgOptionComponent,
        CategorySelectorComponent,
        TagsComponent,
        StickyHeaderDirective,
        AssignBlockComponent,
        TicketToolbarComponent,
        TranslateModule,
        ThreadFilterComponent,
        TicketAlertsComponent,
        LoaderComponent,
        AddReplyNoteComponent,
        TicketThreadWebComponent,
        TicketThreadChatComponent,
        TicketThreadSocialMediaComponent,
        TicketNotesComponent,
        RepliesBulkOperationMenuComponent,
        TicketRightPanelComponent,
        EnumToArrayPipe,
        DetailedTicketSourcePipe,
        SentimentPipe,
    ],
})
export class TicketDetailsComponent implements OnInit, OnDestroy {
    @HostBinding('class.ticket-details-chat') hasChatClass = false;

    @ViewChild(CategorySelectorComponent) categorySelectorComponent: CategorySelectorComponent;
    @ViewChild(StickyHeaderDirective) stickyHeaderElement: StickyHeaderDirective;
    bccUsers: BaseUserWithRole[] = [];
    ccUsers: BaseUserWithRole[] = [];
    copyTicketIdTooltip = this.translateService.instant('Click to copy');
    isCustomerOnline = true;
    isInIframe: boolean;
    isSendingReply = false;
    isSingleTicketView: boolean;
    isStatusUpdating: boolean;
    isUpdatingSpam = false;
    isTicketReady = true;
    loaded: boolean;
    loadedThread: boolean;
    newAttachments: AttachmentBase[] = [];
    newReply = new ReplyUpdateModel();
    rightPanelTypeEnum = RightPanelType;
    sentimentsEnum = Sentiments;
    threadFilterService = ThreadFilterService;
    ticket: DetailedTicketDTO = new DetailedTicketDTO();
    ticketSourceEnum = TicketSource;
    ticketStatusEnum = TicketStatus;
    ticketUpdateStamp: number;
    ticketViewEnum = TicketView;
    ticketViewType: TicketView;

    user: CommunityUserExtended;
    quillRange: Range | null;
    quillEditors: Record<string, any> = {};
    isFacebook: boolean;
    isFb: boolean;
    isForm: boolean;
    isChat: boolean;
    isFormEmailApi: boolean;
    isSocialMediaTicket: boolean;
    isAutoSignature: boolean;
    isOneTime: boolean;
    isShowFullReplyForm: boolean;

    socialMediaSourceId: number;
    messagesOrderEnum = MessagesOrder;
    isUpdatingTagsList: boolean;
    completedOperationId: string;
    isRequestingSessionAuthentication = false;
    readonly isInboxElementVisible = isInboxElementVisible;

    private additionalKey: number | undefined;
    private isTabActive = false;
    private readonly unsubscriber = new Subject<void>();

    get isSessionAuthenticationVisible(): boolean {
        return (
            this.commonDataService.customerSettings?.isSessionAuthenticationEnabled === true &&
            !!this.ticket?.sessionAuthentication &&
            !this.commonDataService.isOneTime
        );
    }

    get isSessionAuthenticationRequestCompleted(): boolean {
        return this.ticket?.sessionAuthentication?.level === SessionAuthenticationLevel.bankIdHigh;
    }

    get isSessionAuthenticationRequestPending(): boolean {
        return (
            !!this.ticket?.sessionAuthentication?.escalationRequestTime && !this.isSessionAuthenticationRequestCompleted
        );
    }

    get isSessionAuthenticationRequestSectionVisible(): boolean {
        return (
            this.isSessionAuthenticationVisible &&
            !this.isSessionAuthenticationRequestCompleted &&
            !this.commonDataService.user?.isReadOnly &&
            this.commonDataService.user?.role !== UserRole.systemAdmin
        );
    }

    get isSessionAuthenticationRequestButtonVisible(): boolean {
        return (
            this.isSessionAuthenticationRequestSectionVisible &&
            !this.isSessionAuthenticationRequestPending &&
            !this.commonDataService.user?.isReadOnly &&
            this.commonDataService.user?.role !== UserRole.systemAdmin &&
            Number(this.ticket.sessionAuthentication?.level) < SessionAuthenticationLevel.bankIdHigh
        );
    }

    get isSessionAuthenticationRequestButtonDisabled(): boolean {
        return this.isRequestingSessionAuthentication;
    }

    constructor(
        private activatedRoute: ActivatedRoute,
        private ticketService: TicketService,
        private tagService: TagService,
        public commonDataService: CommonDataService,
        private bsModalService: BsModalService,
        private componentMessengerService: ComponentMessengerService,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
        public rightPanelService: RightPanelService,
        public ticketRightPanelService: TicketRightPanelService,
        public replyStoreService: ReplyStoreService,
        private replyDataService: ReplyDataService,
        private signalRService: SignalRService,
        public noteService: NoteService,
        public ticketDetailsPanelService: TicketDetailsPanelService,
        public replyTemplateManagerService: ReplyTemplateManagerService,
        private quillHelperService: QuillHelperService,
        private replyService: ReplyService,
        private signatureService: SignatureService,
        private replyMentionsToCodePipe: ReplyMentionsToCodePipe,
        private smilesService: SmilesService,
        private superOfficeService: SuperOfficeService,
        public assignService: AssignService,
        private userService: UserService,
        private newLinePipe: NewLinePipe,
        private fieldsService: FieldsService,
        public replySortingService: ReplySortingService,
        private location: Location,
        public openTicketInNewWindowService: OpenTicketInNewWindowService,
        private fieldsLogService: FieldsLogService,
    ) {}

    ngOnInit(): void {
        if (this.activatedRoute.snapshot.routeConfig?.data.stateName === 'ticket') {
            this.isSingleTicketView = true;
        }

        this.isInIframe = HelperService.checkIsInIframe();

        if (
            this.activatedRoute.snapshot.queryParams['ticketId'] &&
            this.activatedRoute.snapshot.queryParams['ticketId'] !== 'undefined'
        ) {
            this.ticket = new DetailedTicketDTO();
            this.ticket.id = +this.activatedRoute.snapshot.queryParams['ticketId'];

            this.ticketDetailsPanelService.replyIdToRemove = this.activatedRoute.snapshot.queryParams.replyId;

            this.fieldsService.currentFields = [];
            this.fieldsService.ticketCreatorId = '';
            this.fieldsService.isGotFields = false;

            this.loaded = false;
            this.ccUsers = [];
            this.isShowFullReplyForm = false;
            this.threadFilterService.resetFilters();

            if (this.rightPanelService.isRightPanelOpened) {
                this.rightPanelService.close();
            }

            this.loadTicket(this.ticket.id);

            this.ticketDetailsPanelService.currentTicketId = this.ticket.id;
            this.loadUsersForMention();

            this.processTicketChanged(this.ticket.id);

            if (this.commonDataService.isTicketTakenOverFromBot) {
                setTimeout(() => {
                    this.showFullReplyForm();
                    this.commonDataService.isTicketTakenOverFromBot = false;
                }, 1000);
            }

            this.newReply = new ReplyUpdateModel();

            this.handleKeyDownClick = this.handleKeyDownClick.bind(this);
            this.handleKeyUpClick = this.handleKeyUpClick.bind(this);
            document.addEventListener('keydown', this.handleKeyDownClick);
            document.addEventListener('keyup', this.handleKeyUpClick);

            this.ticketService.onTicketRedactedSubject
                .pipe(takeUntil(this.unsubscriber))
                .subscribe(({ ticket, operationId }) => {
                    if (this.ticket && ticket.id === this.ticket.id) {
                        if (operationId && this.completedOperationId === operationId) {
                            return;
                        }
                        this.completedOperationId = operationId;
                        this.loadedThread = this.loaded = false;
                        this.isTicketReady = false;
                        this.processLoadTicket(ticket);
                        this.ticketService.isRedacting = false;
                    }
                });

            this.user = this.commonDataService.user;
            this.isOneTime = this.commonDataService.isOneTime;

            this.setupSignalRListeners();

            this.noteService.updateStamp.pipe(takeUntil(this.unsubscriber)).subscribe(() => {
                this.threadFilterService.refreshUpdateStamp();
            });

            this.componentMessengerService.updateTeamMembers.pipe(takeUntil(this.unsubscriber)).subscribe((data) => {
                this.ticket.teammatesWithAccess = this.assignService.sortUsersForAssign(data);
                this.assignService.usersForAssign = this.assignService.sortUsersForAssign(
                    this.ticket.teammatesWithAccess,
                );
            });
        }
    }

    private handleKeyDownClick(event: KeyboardEvent): void {
        if (this.isTabActive) {
            event.preventDefault();
            event.stopPropagation();
        }

        const isClickTargetNotEditor =
            event.target &&
            !(event.target as HTMLElement).classList.contains('ql-editor') &&
            (event.target as HTMLElement).localName !== 'input' &&
            !(event.target as HTMLElement).classList.contains('modal');

        const isModalWindowClosed = document.getElementsByClassName('modal').length === 0;

        if (isClickTargetNotEditor && isModalWindowClosed) {
            this.handlePressedKeys(event, true);

            if (this.isTabActive && this.additionalKey) {
                switch (this.additionalKey) {
                    case KEY_C: {
                        // update ticket status to closed
                        this.updateStatus(this.ticketStatusEnum.completed);
                        break;
                    }

                    case KEY_S: {
                        // Focus on search tickets field
                        const searchBar = document.getElementById('search-bar');
                        if (searchBar) {
                            searchBar.focus();
                        }
                        event.preventDefault();
                        break;
                    }

                    case KEY_A: {
                        // show list with users for assigning
                        this.assignService.showAssigningSelect(event);
                        break;
                    }

                    case KEY_L: {
                        // assign ticket to current user
                        this.assignTicket({
                            event,
                            data: {
                                isAssigned: true,
                            },
                        });
                        break;
                    }

                    case KEY_N: {
                        // open notes tab
                        if (this.commonDataService.user.hasAccessToNotes) {
                            this.noteService.openNotes(this.ticket);
                        }
                        break;
                    }

                    case KEY_R: {
                        // open new reply field
                        if (this.ticket.isTicketCanBeReplied) {
                            this.showFullReplyForm(true);
                            event.preventDefault();
                        }
                        break;
                    }
                }

                this.resetPressedKeys();
            }
        }

        this.additionalKey = undefined;
    }

    private handlePressedKeys(event: KeyboardEvent, isKeyDown: boolean): void {
        if (event.keyCode === TAB) {
            this.isTabActive = isKeyDown;
            this.additionalKey = undefined;
        } else {
            this.additionalKey = event.keyCode;
        }
    }

    private loadUsersForMention(): void {
        this.userService
            .getAll()
            .pipe(takeUntil(this.unsubscriber), first())
            .subscribe((data) => {
                this.noteService.allUsers = data;
            });
    }

    private resetPressedKeys(): void {
        // Need this logic for case when user pressed key Tab and focus was set out of listener scope
        this.isTabActive = false;
        this.additionalKey = undefined;
    }

    private handleKeyUpClick(e: KeyboardEvent): void {
        this.handlePressedKeys(e, false);
    }

    back(): void {
        this.location.back();
    }

    onEditorCreated(quill: any, selector: string): void {
        if (this.isFormEmailApi) {
            quill.getModule('toolbar').addHandler('image', () => {
                this.quillHelperService.selectLocalImage(quill);
            });
        }

        this.quillEditors[selector] = quill;

        if (selector === 'newReply') {
            this.initTicketReplyUser();
        }
    }

    // todo: it is used only in SoMe
    openNewPrivateReplyForm(reply: ThreadElementDTO): void {
        const initialState = {
            reply,
            ticket: this.ticket,
            socialMediaSourceId: this.socialMediaSourceId,
        };

        this.bsModalService.show(AddPrivateReplyModalComponent, {
            initialState,
            ignoreBackdropClick: true,
            class: 'private-reply-form-modal',
        });
    }

    async addReply(
        reply: ReplyUpdateModel,
        asDraft = false,
        isCloseTicket = false,
        ccUsers: UserWithLabelModel[] = [],
        bccUsers: UserWithLabelModel[] = [],
        toEmails: UserWithLabelModel[] = [],
        usersToMention?: BaseUserWithRole[],
        isShouldBeLoggedInCrm?: boolean,
        parentReply?: ThreadElementDTO,
        isReplySentAsLink?: boolean,
        outboundEmailAddress?: string,
    ): Promise<void> {
        if (this.commonDataService.isOneTime) {
            const isReplyAddingConfirmed = await lastValueFrom(this.addReplyFromOnetimeLinkConfirmation());

            if (!isReplyAddingConfirmed) {
                return;
            }
        }

        let loggedReplyModel: SendTicketToSORequest;
        if (isShouldBeLoggedInCrm) {
            loggedReplyModel = await lastValueFrom(this.logToCrmConfirmation());

            if (!loggedReplyModel) {
                return;
            }
        }

        if ((ccUsers?.length || toEmails?.length || bccUsers?.length) && !reply.isDraft) {
            const ccUsersEmails = ccUsers.map((u) => u.email);
            const bccUsersEmails = bccUsers.map((u) => u.email);
            const toUsersEmails = this.commonDataService.isOneTime
                ? this.ticket.defaultReplyRecipients.to
                : toEmails.map((u) => u.email);
            const isCcFieldNotUpdated = HelperService.compareStringArrays(
                ccUsersEmails,
                this.ticket.defaultReplyRecipients.cc,
            );
            const isBccFieldNotUpdated =
                bccUsers.length === 0 ||
                HelperService.compareStringArrays(bccUsersEmails, this.ticket.defaultReplyRecipients.bcc);
            const isToFieldNotUpdated = HelperService.compareStringArrays(
                toUsersEmails,
                this.ticket.defaultReplyRecipients.to,
            );

            if (!isCcFieldNotUpdated || !isBccFieldNotUpdated || !isToFieldNotUpdated) {
                reply.isRecipientChangeConfirmed = await lastValueFrom(
                    this.addUpdateEmailsConfirmation(
                        ccUsersEmails,
                        toUsersEmails,
                        bccUsersEmails,
                        isCcFieldNotUpdated,
                        isToFieldNotUpdated,
                        isBccFieldNotUpdated,
                    ),
                );
                if (!reply.isRecipientChangeConfirmed) {
                    return;
                }
            }
        }

        reply.isDraft = asDraft;
        reply.isCompleted = isCloseTicket;
        reply.usersToMention = usersToMention;

        if (reply.isCompleted) {
            if (!this.ticket.category && this.commonDataService.customerSettings.isTicketCategoryRequired) {
                this.categorySelectorComponent.openCategoriesModal(
                    this.translateService.instant('Ticket must have category to be closed.'),
                    false,
                    () => {
                        this.addReply(
                            reply,
                            asDraft,
                            isCloseTicket,
                            ccUsers,
                            bccUsers,
                            toEmails,
                            usersToMention,
                            isShouldBeLoggedInCrm,
                            parentReply,
                            isReplySentAsLink,
                            outboundEmailAddress,
                        );
                    },
                );
                return;
            }
            if (
                this.ticket.tags.length === 0 &&
                this.commonDataService.customerSettings.isTicketTagRequiredForClosing
            ) {
                this.openAddTagsModal(
                    undefined,
                    this.translateService.instant('Ticket must have tags to be closed.'),
                    false,
                    () => {
                        this.addReply(
                            reply,
                            asDraft,
                            isCloseTicket,
                            ccUsers,
                            bccUsers,
                            toEmails,
                            usersToMention,
                            isShouldBeLoggedInCrm,
                            parentReply,
                            isReplySentAsLink,
                            outboundEmailAddress,
                        );
                    },
                );
                return;
            }
        }

        let newToEmails: string[] = structuredClone(this.ticket.defaultReplyRecipients.to);
        if (
            (this.ticket.source === TicketSource.email ||
                this.ticket.source === TicketSource.form ||
                this.ticket.source === TicketSource.api) &&
            !this.commonDataService.isOneTime
        ) {
            newToEmails = toEmails.map((u) => u.email);
        }

        const newCcEmails: string[] = ccUsers.map((u) => u.email);
        const newBccEmails: string[] = bccUsers.map((u) => u.email);

        const oldStatus = this.ticket.status;

        const newReplyModel: ReplyUpdateModel = {
            attachments: reply.attachments,
            isDraft: reply.isDraft,
            isReplySentAsLink,
            usersToMention: reply.usersToMention,
            message: new MessageDTO(
                this.isFb ? null : reply.message.html,
                // todo: rewrite this conditions
                /* eslint-disable */
                this.isFormEmailApi
                    ? null
                    : this.ticket.constraints.isUserMentioningAllowed
                      ? HelperService.strip(
                            this.smilesService.replaceImagesToCodes(
                                this.replyMentionsToCodePipe.transform(reply.message.html),
                            ),
                            false,
                        )
                      : reply.message.text,
                /* eslint-enable */
            ),
            isCompleted: reply.isCompleted,
            isRecipientChangeConfirmed: reply.isRecipientChangeConfirmed,
            isNeedToBeSentByEmail: reply.isNeedToBeSentByEmail,
            parentId: reply.parentId,
            senderEmail: this.user.email,
            senderName: this.user.name,
            toEmails: newToEmails,
            ccEmails: newCcEmails,
            bccEmails: newBccEmails,
            receiverEmail: null,
            socialMediaSourceId: this.socialMediaSourceId,
            suggestionId: reply.suggestionId,
            ticketId: this.ticket.id,
            outboundEmailAddress: outboundEmailAddress ? outboundEmailAddress : null,
        };

        if (parentReply) {
            parentReply.isSendingReply = true;
        } else {
            this.isSendingReply = true;
        }

        this.replyStoreService.updateTempReplyStatus(true, this.ticket.id, newReplyModel.parentId);

        const addReplyObservable = this.replyDataService.addReply(this.ticket.id, newReplyModel).pipe(share());

        if (newReplyModel.message.html) {
            newReplyModel.message.html = newReplyModel.message.html.replaceAll('<div></div>', '<div><br></div>');
        }
        addReplyObservable.pipe(takeUntil(this.unsubscriber)).subscribe(
            (data) => {
                if (reply.isDraft) {
                    this.notificationsService.success(
                        this.translateService.instant('Success!'),
                        this.translateService.instant('Your draft was successfully saved.'),
                    );
                } else if (reply.isCompleted) {
                    if (
                        this.ticket.source === TicketSource.email ||
                        this.ticket.source === TicketSource.form ||
                        this.ticket.source === TicketSource.api
                    ) {
                        this.ticket.defaultReplyRecipients.to = newToEmails;
                    }
                    this.ticket.defaultReplyRecipients.cc = newCcEmails;
                    this.notificationsService.success(
                        this.translateService.instant('Success!'),
                        this.translateService.instant('Your reply was sent and ticket status set to Completed.'),
                    );
                    this.applyReturnedStatus(TicketStatus.completed, oldStatus);
                } else {
                    if (
                        this.ticket.source === TicketSource.email ||
                        this.ticket.source === TicketSource.form ||
                        this.ticket.source === TicketSource.api
                    ) {
                        this.ticket.defaultReplyRecipients.to = newToEmails;
                    }
                    this.ticket.defaultReplyRecipients.cc = newCcEmails;
                    if (!this.isChat) {
                        if (this.commonDataService.isOneTime) {
                            this.notificationsService.success(
                                this.translateService.instant('Good work!'),
                                this.translateService.instant('Your customer has received your reply.'),
                            );
                        } else {
                            this.notificationsService.success(
                                this.translateService.instant('Success!'),
                                this.translateService.instant('Your reply was successfully sent.'),
                            );
                        }
                    }
                }

                if (this.isFormEmailApi) {
                    this.bccUsers = [];
                }

                if (this.isFormEmailApi && reply.isDraft) {
                    data.ccUsers = structuredClone(ccUsers);
                }

                this.replyService.processNewReply(this.ticket, data);

                if (reply.parentId) {
                    ReplyUpdateModel.reset(reply);
                    reply.socialMediaSourceId = this.socialMediaSourceId;

                    if (parentReply) {
                        parentReply.isSendingReply = false;
                    }

                    parentReply.isShowNewSubreplyForm = false;
                } else {
                    if (this.ticketViewType === TicketView.chat) {
                        this.componentMessengerService.addNewReply(data);
                    }

                    ReplyUpdateModel.reset(reply);

                    setTimeout(() => {
                        this.initTicketReplyUser();
                    }, 50);

                    this.newAttachments.length = 0;
                    this.isSendingReply = false;
                }

                this.replyService.updateThreadLastUpdateTime(
                    this.ticket.messageThread,
                    data.threadLastUpdateTime,
                    data.parentReplyIds,
                );
                this.threadFilterService.refreshUpdateStamp();

                if (loggedReplyModel) {
                    this.saveToSuperOffice(loggedReplyModel);
                }
            },
            () => {
                if (parentReply) {
                    parentReply.isSendingReply = false;
                } else {
                    this.isSendingReply = false;
                }

                this.ticket.status = oldStatus;
            },
        );

        addReplyObservable.pipe(takeUntil(this.unsubscriber), take(1)).subscribe(
            (data) => {
                this.replyStoreService.clearTempReply(data.parentReplyId, data.ticketId);
            },
            () => {
                this.replyStoreService.updateTempReplyStatus(false, this.ticket.id, newReplyModel.parentId);
            },
        );
    }

    private saveToSuperOffice(loggedReplyModel: SendTicketToSORequest): void {
        this.superOfficeService
            .save(loggedReplyModel)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(() => {
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('This reply was successfully logged in SuperOffice.'),
                );
            });
    }

    updateCopyTicketIdTooltip(tooltipDirective: TooltipDirective): void {
        this.copyTicketIdTooltip = this.translateService.instant('Copied');
        tooltipDirective.hide();
        setTimeout(() => {
            tooltipDirective.show();
        }, 200);

        setTimeout(() => {
            this.copyTicketIdTooltip = this.translateService.instant('Click to copy');
        }, 1000);
    }

    updateQuillSelection(quill: any): void {
        this.quillRange = this.replyTemplateManagerService.updateRange(quill);
    }

    assignTicket(event): void {
        const isAssigned = event.data.isAssigned;
        let user = event.data.assignee;

        if (isAssigned && !user) {
            user = this.commonDataService.user;
        }

        if (
            !this.commonDataService.user.isAdmin &&
            !(
                this.commonDataService.customerSettings.isStaffHasRightToChangeTicketAssignee ||
                this.ticket.assignerId === this.commonDataService.user.id
            ) &&
            this.ticket.assignee != null &&
            this.commonDataService.user.id !== this.ticket.assignee.id
        ) {
            this.assignService.isAssigningTicket = false;
            alert(this.translateService.instant('You cannot reassign a ticket assigned to another user'));
        } else {
            this.assignService.isAssigningTicket = true;

            let previousAssigneeId;
            let newAssigneeId;

            if (this.ticket.isAssigned) {
                previousAssigneeId = this.ticket.assignee?.id;
            }

            if (isAssigned) {
                newAssigneeId = user.id;
            }

            this.ticketService
                .assign(this.ticket.id, previousAssigneeId, newAssigneeId)
                .pipe(
                    finalize(() => {
                        this.assignService.isAssigningTicket = false;
                    }),
                    takeUntil(this.unsubscriber),
                )
                .subscribe((assigneeUpdateData) => {
                    this.ticket.isAssigned = assigneeUpdateData.isAssigned;
                    this.ticket.assignee = assigneeUpdateData.assignee;
                    this.ticket.assignerId = assigneeUpdateData.assignerId;

                    this.ticket.isTicketCanBeReplied = this.replyService.getIsTicketCanBeRepliedStatus(
                        this.ticket,
                        this.user.id,
                        this.isCustomerOnline,
                    );
                    this.ticket.isReplyCanBeEdited = this.replyService.getIsReplyCanBeEditedStatus(
                        this.ticket,
                        this.user.id,
                    );

                    if (this.ticket.isAssigned && this.ticket.assignee?.id === this.commonDataService.user.id) {
                        this.showFullReplyForm();
                    }
                });
        }
    }

    markTicketAsSpam() {
        this.isUpdatingSpam = true;
        const oldStatus = this.ticket.status;

        const newSpamStatus = !this.ticket.isSpam;

        this.ticketService
            .spam(this.ticket.id, newSpamStatus)
            .pipe(
                finalize(() => {
                    this.isUpdatingSpam = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe(() => {
                this.ticket.isSpam = newSpamStatus;
                this.applyReturnedStatus(TicketStatus.completed, oldStatus);
            });
    }

    openAddTagsModal(
        reply?: ThreadElementDTO,
        alertMessage?: string,
        isTicketShouldBeClosedAfter = false,
        callback?: () => void,
        isHandled?: boolean,
    ): void {
        if (
            this.ticket.assignee &&
            this.ticket.assignee?.id !== this.commonDataService.user.id &&
            !this.commonDataService.user.isAdmin &&
            !this.commonDataService.user.isManager
        ) {
            this.notificationsService.error(
                this.translateService.instant('Error!'),
                this.translateService.instant('Ticket is assigned to another user.'),
            );
            return;
        }
        const initialState = {
            entity: reply ? reply : this.ticket,
            tags: this.tagService.availableTagsForTicket,
            confirmCallback: (tags) => {
                return reply
                    ? this.updateReplyTags(reply, tags)
                    : this.updateTags(tags, isTicketShouldBeClosedAfter, callback, isHandled);
            },
            isReply: !!reply,
            alertMessage,
        };

        this.bsModalService.show(AddTagsModalComponent, {
            initialState,
        });
    }

    openNoteEditor(reply: ThreadElementDTO, isChatView?: boolean): void {
        this.noteService.openEditor(reply, isChatView);
    }

    openAttachmentsModal(
        newAttachments: AttachmentBase[],
        existedAttachmentsLength: number,
        maxAttachmentsCount?: number,
    ) {
        const initialState = {
            newAttachments,
            existedAttachmentsLength,
            maxAttachmentsCount,
        };

        this.bsModalService.show(AttachmentsModalComponent, {
            initialState,
            ignoreBackdropClick: true,
        });
    }

    openReplyTemplatesModal($event: Event, quillEditor: any, reply: ReplyUpdateModel): void {
        this.updateQuillSelection(quillEditor);
        const initialState = {
            isTextContent: this.isFb,
            confirmCallback: (templateText, attachments?: InboxAttachmentBase[]) => {
                return this.replyTemplateManagerService.chooseReplyTemplate({
                    $event,
                    templateText: this.isFb ? this.newLinePipe.transform(templateText) : templateText,
                    quillEditor,
                    quillRange: this.quillRange,
                    reply,
                    attachments,
                });
            },
        };

        this.bsModalService.show(ReplyTemplatesModalComponent, {
            initialState,
        });
    }

    restore(): void {
        this.updateStatus(TicketStatus.open, undefined, undefined, true);
    }

    showFullReplyForm(isReplyOnlyToSender?: boolean): void {
        this.isShowFullReplyForm = true;

        if (this.isFormEmailApi) {
            if (isReplyOnlyToSender) {
                this.ticket.defaultReplyRecipients.cc.length = 0;
                this.ccUsers.length = 0;
            }
        }

        setTimeout(() => {
            this.replyService.initializeNewReplyEditor();

            if (this.replyService.newReplyEditor) {
                this.replyService.newReplyEditor.closest('.new-reply')?.scrollIntoView({ behavior: 'smooth' });
                this.replyService.newReplyEditor.focus();
            }
        }, 100);
    }

    updateSentiment(): void {
        this.ticketService
            .sentiment(this.ticket.id, this.ticket.sentiment)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe();
    }

    updateStatus(
        status: TicketStatus,
        isIgnoreRequirements?: boolean,
        isHandled?: boolean,
        isShouldBeRestoredFromSpam?: boolean,
    ): void {
        if (this.ticket.assignee && this.ticket.assignee.id !== this.commonDataService.user.id) {
            this.notificationsService.error(
                this.translateService.instant('Error!'),
                this.translateService.instant('Ticket is assigned to another user.'),
            );
            return;
        }

        if (status === TicketStatus.completed) {
            if (
                !isIgnoreRequirements &&
                this.commonDataService.customerSettings.handleSettings.isEnabled &&
                this.ticketViewType === TicketView.socialMedia &&
                this.commonDataService.customerSettings.handleSettings.isUnhandledRepliesNotificationEnabled &&
                this.ticket.filteredRepliesCount.unhandled > 0
            ) {
                const initialState = {
                    confirmCallback: (isMarkAsHandled: boolean) => {
                        this.updateStatus(TicketStatus.completed, true, isMarkAsHandled);
                    },
                };

                this.bsModalService.show(CloseTicketWithUnhandledRepliesModalComponent, {
                    initialState,
                });

                return;
            }

            if (!this.ticket.category && this.commonDataService.customerSettings.isTicketCategoryRequired) {
                if (this.isOneTime) {
                    this.notificationsService.error(
                        this.translateService.instant('Error!'),
                        this.translateService.instant('Not enough permissions.'),
                    );
                    return;
                }
                this.categorySelectorComponent.openCategoriesModal(
                    this.translateService.instant('Ticket must have category to be closed.'),
                    true,
                    () => {
                        return;
                    },
                    isHandled,
                );
                return;
            }

            if (
                this.ticket.tags.length === 0 &&
                this.commonDataService.customerSettings.isTicketTagRequiredForClosing
            ) {
                if (this.isOneTime) {
                    this.notificationsService.error(
                        this.translateService.instant('Error!'),
                        this.translateService.instant('Not enough permissions.'),
                    );
                    return;
                }
                this.openAddTagsModalForClosingTicket(isHandled);
                return;
            }
        }

        this.isStatusUpdating = true;
        const previousStatus = this.ticket.status;
        this.ticketService
            .updateStatus(this.ticket.id, status, isHandled, isShouldBeRestoredFromSpam)
            .pipe(
                finalize(() => {
                    this.isStatusUpdating = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe(() => {
                this.applyReturnedStatus(status, previousStatus);
            });
    }

    updateTags(tags: Tag[], isTicketShouldBeClosedAfter: boolean, callback?: () => void, isHandled?: boolean) {
        const oldStatus = this.ticket.status;
        this.ticketService
            .updateTags(this.ticket.id, tags, isTicketShouldBeClosedAfter, isHandled)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((result) => {
                this.ticket.tags = result.tags;
                this.applyReturnedStatus(result.status, oldStatus);

                if (callback) {
                    callback();
                }
            });
    }

    updateReplyTags(reply: ThreadElementDTO, tags: Tag[]): void {
        this.replyDataService
            .updateTags(this.ticket.id, reply.id, tags)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((newTags) => {
                reply.tags = newTags;
            });
    }

    private initTicketReplyUser(): void {
        this.ticket.isTicketCanBeReplied = this.replyService.getIsTicketCanBeRepliedStatus(
            this.ticket,
            this.user.id,
            this.isCustomerOnline,
        );

        if (
            !this.newReply.message.text &&
            !this.replyStoreService.isReplyHasOnlyInlineImage(this.newReply.message.html) &&
            this.isAutoSignature
        ) {
            setTimeout(() => {
                this.setSignature(
                    this.newReply,
                    null,
                    { target: document.getElementById('btn-signature') as HTMLElement },
                    !this.ticket.isAssigned,
                );
                this.quillEditors['newReply'].setSelection(0);
            }, 100);
        }
    }

    // todo: move to signature service?
    setSignature(
        reply: ReplyUpdateModel | null,
        parentReplyId: number | null,
        event: any,
        isFocusNotNeeded?: boolean,
    ): void {
        const signature =
            this.ticket.source === TicketSource.email ||
            this.ticket.source === TicketSource.form ||
            this.ticket.source === TicketSource.api
                ? this.signatureService.getSignature(this.ticket.source)
                : this.newLinePipe.transform(this.signatureService.getSignature(this.ticket.source));
        let replySelector: string;

        if ('id' in reply) {
            replySelector = 'reply' + reply.id;
        } else {
            if (parentReplyId) {
                replySelector = 'newSubReply' + parentReplyId;
            } else {
                replySelector = 'newReply';
            }
        }

        this.replyTemplateManagerService.chooseReplyTemplate({
            $event: event,
            templateText: signature,
            quillEditor: this.quillEditors[replySelector],
            quillRange: null,
            isSmile: false,
            isSetCaretAtStart: !!reply,
            isFocusNotNeeded,
        });
        this.quillEditors[replySelector].setSelection(0);
    }

    deleteReply(reply: ThreadElementDTO, parentReply?: ThreadElementDTO): void {
        const isSubReply = !!parentReply;

        this.replyDataService
            .deleteReply(this.ticket.id, isSubReply ? parentReply.id : null, reply.id)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((deletedReplyModel) => {
                if (reply.isDraft) {
                    this.processDeletedDraft(reply.id, parentReply);
                } else {
                    this.processUpdatedReply(deletedReplyModel.reply, true);
                }
            });
    }

    async doneEditing(
        parentReplyId,
        replyOrSubReply,
        shouldBeClosed,
        asDraft,
        ccUsers,
        bccUsers,
        toEmails?,
        usersToMention?,
        isShouldBeLoggedInCrm?: boolean,
        isReplySentAsLink?,
        outboundEmailAddress?: string,
    ) {
        let loggedReplyModel: SendTicketToSORequest;
        if (isShouldBeLoggedInCrm) {
            loggedReplyModel = await lastValueFrom(this.logToCrmConfirmation());

            if (!loggedReplyModel) {
                return;
            }
        }

        if (
            (ccUsers?.length || toEmails?.length || bccUsers?.length) &&
            !asDraft &&
            !this.commonDataService.isOneTime
        ) {
            // todo: duplicated code fragment
            const ccUsersEmails = ccUsers.map((u) => u.email);
            const bccUsersEmails = bccUsers.map((u) => u.email);
            const toUsersEmails = toEmails.map((u) => u.email);
            const isCcFieldNotUpdated = HelperService.compareStringArrays(
                ccUsersEmails,
                this.ticket.defaultReplyRecipients.cc,
            );
            const isBccFieldNotUpdated =
                bccUsers.length === 0 ||
                HelperService.compareStringArrays(bccUsersEmails, this.ticket.defaultReplyRecipients.bcc);
            const isToFieldNotUpdated = HelperService.compareStringArrays(
                toUsersEmails,
                this.ticket.defaultReplyRecipients.to,
            );

            if (!isCcFieldNotUpdated || !isBccFieldNotUpdated || !isToFieldNotUpdated) {
                replyOrSubReply.isRecipientChangeConfirmed = await lastValueFrom(
                    this.addUpdateEmailsConfirmation(
                        ccUsersEmails,
                        toUsersEmails,
                        bccUsersEmails,
                        isCcFieldNotUpdated,
                        isToFieldNotUpdated,
                        isBccFieldNotUpdated,
                    ),
                );
                if (!replyOrSubReply.isRecipientChangeConfirmed) {
                    return;
                }
            }
        }

        if (
            this.ticket.source === TicketSource.email ||
            this.ticket.source === TicketSource.form ||
            this.ticket.source === TicketSource.api
        ) {
            replyOrSubReply.toEmails = toEmails.map((u) => u.email);
        }

        const toEmailsList =
            this.ticket.source === TicketSource.email ||
            this.ticket.source === TicketSource.form ||
            this.ticket.source === TicketSource.api
                ? replyOrSubReply.toEmails
                : this.ticket.defaultReplyRecipients.to;

        replyOrSubReply.ccEmails = ccUsers.map((u) => u.email);
        replyOrSubReply.bccUsers = bccUsers.map((u) => u.email);
        replyOrSubReply.message = new MessageDTO(
            this.isFb ? null : replyOrSubReply.message.html,
            // todo: rewrite this conditions
            /* eslint-disable */
            this.isFormEmailApi
                ? null
                : this.ticket.constraints.isUserMentioningAllowed
                  ? HelperService.strip(
                        this.smilesService.replaceImagesToCodes(
                            this.replyMentionsToCodePipe.transform(replyOrSubReply.message.html),
                        ),
                        false,
                    )
                  : replyOrSubReply.message.text,
            /* eslint-enable */
        );

        replyOrSubReply.isSendingReply = true;
        replyOrSubReply.socialMediaSourceId = this.socialMediaSourceId;

        if (usersToMention) {
            replyOrSubReply.usersToMention = usersToMention;
        }

        const wasDraft = replyOrSubReply.isDraft;
        replyOrSubReply.isDraft = asDraft;

        if (wasDraft && !asDraft) {
            replyOrSubReply.creationTime = new Date();
        }
        const replyModel = new ReplyUpdateModel(
            replyOrSubReply,
            parentReplyId,
            replyOrSubReply.ccEmails,
            toEmailsList,
            replyOrSubReply.bccUsers,
            !this.isFormEmailApi,
        );

        replyModel.outboundEmailAddress = outboundEmailAddress || null;
        replyModel.parentId = parentReplyId;
        replyModel.ticketId = this.ticket.id;
        replyModel.isReplySentAsLink = isReplySentAsLink;
        replyModel.isRecipientChangeConfirmed = replyOrSubReply.isRecipientChangeConfirmed;

        if (shouldBeClosed) {
            replyOrSubReply.attachments = replyModel.attachments;
            this.replyService.processAttachments(replyOrSubReply);
            replyOrSubReply.editingReply = false;
        }

        if (replyModel.message.html) {
            replyModel.message.html = replyModel.message.html.replaceAll('<div></div>', '<div><br></div>');
        }

        this.replyDataService
            .updateReply(this.ticket.id, replyOrSubReply.id, replyModel)
            .pipe(
                finalize(() => {
                    replyOrSubReply.isSendingReply = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((data) => {
                data.subReplies = replyOrSubReply.subReplies;

                if (wasDraft && !asDraft) {
                    this.ticket.defaultReplyRecipients.cc = replyModel.ccEmails;
                    this.ticket.defaultReplyRecipients.bcc = replyModel.bccEmails;
                    if (
                        this.ticket.source === TicketSource.email ||
                        this.ticket.source === TicketSource.form ||
                        this.ticket.source === TicketSource.api
                    ) {
                        this.ticket.defaultReplyRecipients.to = replyModel.toEmails;
                    }
                    if (parentReplyId) {
                        const parentReply = this.replyService.findReply(this.ticket.messageThread, parentReplyId);

                        const tempReplies = parentReply.subReplies.filter((reply) => {
                            return reply.id !== data.id;
                        });

                        parentReply.subReplies.length = 0;
                        parentReply.subReplies = parentReply.subReplies.concat(tempReplies);

                        this.replyService.processNewReply(this.ticket, data);
                    } else {
                        const tempReplies = this.ticket.messageThread.filter((reply) => {
                            return reply.id !== data.id;
                        });

                        this.ticket.messageThread.length = 0;

                        Array.prototype.push.apply(this.ticket.messageThread, tempReplies);

                        this.replyService.processNewReply(this.ticket, data);
                    }

                    if (!this.isChat) {
                        if (this.commonDataService.isOneTime) {
                            this.notificationsService.success(
                                this.translateService.instant('Good work!'),
                                this.translateService.instant('Your customer has received your reply.'),
                            );
                        } else {
                            this.notificationsService.success(
                                this.translateService.instant('Success!'),
                                this.translateService.instant('Your reply was successfully sent.'),
                            );
                        }
                    }

                    this.threadFilterService.refreshUpdateStamp();
                } else {
                    Object.assign(replyOrSubReply, data);
                    replyOrSubReply = this.replyService.processReply(replyOrSubReply);
                }

                this.replyService.updateThreadLastUpdateTime(
                    this.ticket.messageThread,
                    data.threadLastUpdateTime,
                    data.parentReplyIds,
                );

                if (loggedReplyModel) {
                    this.saveToSuperOffice(loggedReplyModel);
                }
            });
    }

    chooseSuggestion(suggestion: { id: number; text: string }): void {
        this.newReply.message.html = suggestion.text;
        this.newReply.message.text = suggestion.text;
        this.newReply.suggestionId = suggestion.id;

        this.showFullReplyForm();
    }

    openAddTagsModalForClosingTicket(isHandled?: boolean): void {
        this.openAddTagsModal(
            undefined,
            this.translateService.instant('Ticket must have tags to be closed.'),
            true,
            () => {
                return;
            },
            isHandled,
        );
    }

    processLoadTicket(ticket: DetailedTicketDTO): void {
        this.isSocialMediaTicket = false;

        this.ticket = ticket;

        this.fieldsLogService.fieldsFromTicket = ticket.fields;
        this.ticketDetailsPanelService.currentTicket = ticket;
        this.replyService.processAttachments(this.ticket);

        this.ticket.teammatesWithAccess = this.assignService.sortUsersForAssign(this.ticket.teammatesWithAccess);

        this.assignService.usersForAssign = this.assignService.sortUsersForAssign(this.ticket.teammatesWithAccess);

        // flags of ticket type

        // @todo: isFb => Bad name for variable. Maybe better to use !this.isFormEmailApi instead?
        this.isFb =
            this.ticket.source !== TicketSource.form &&
            this.ticket.source !== TicketSource.email &&
            this.ticket.source !== TicketSource.api;
        this.isForm = this.ticket.source === TicketSource.form;
        this.isChat = this.ticket.source === TicketSource.chat;
        this.isFormEmailApi =
            this.ticket.source === TicketSource.form ||
            this.ticket.source === TicketSource.email ||
            this.ticket.source === TicketSource.api;

        this.isFacebook = this.ticket.source === TicketSource.facebook;

        this.setTicketViewType();
        this.ticket.ticketViewType = this.ticketViewType;

        this.replyService.isOnlyFb = this.ticket.source === TicketSource.facebook;
        this.hasChatClass = this.isChat;

        const tempReply = this.replyStoreService.getTempReply(this.ticket.id);

        if (tempReply) {
            this.newReply.message.text = tempReply.text;
            this.newReply.message.html = tempReply.html;
            this.isShowFullReplyForm = true;

            setTimeout(() => {
                this.replyService.initializeNewReplyEditor();
            }, 100);
        }

        this.replyService.processReplies(this.ticket);

        if (this.commonDataService.user.role === UserRole.vendor) {
            this.ticket.isAssigned = false;
            this.ticket.assignee = null;
        }

        this.isAutoSignature = this.signatureService.getSignatureStatus(this.ticket.source);
        this.replyStoreService.userSignature =
            this.ticket.source === TicketSource.email ||
            this.ticket.source === TicketSource.form ||
            this.ticket.source === TicketSource.api
                ? this.signatureService.getSignature(this.ticket.source)
                : this.newLinePipe.transform(this.signatureService.getSignature(this.ticket.source));

        try {
            (this.newReply as any).socialMediaSourceId = this.socialMediaSourceId;
        } catch {
            // Silently handle error if property doesn't exist
        }

        if (!this.commonDataService.isOneTime) {
            const rightPanelTab = this.activatedRoute.snapshot.queryParams.rightPanelTab;
            const noteId = this.activatedRoute.snapshot.queryParams.noteId;
            const isNotesVisible = this.isNotesTabVisible();
            const isLogVisible = this.isLogTabVisible();

            if (
                this.commonDataService.user.hasAccessToNotes &&
                isNotesVisible &&
                noteId &&
                !this.noteService.ignoreParamNoteId &&
                +noteId !== this.noteService.noteIdWithMentionedUser
            ) {
                // check: if user haven't seen note(where he mentioned) yet, then he'll be redirected to right panel with note
                this.noteService.noteIdWithMentionedUser = +noteId;
                this.ticketRightPanelService.open({ ticket: this.ticket, tabType: TicketTabType.notes }, false, false);
            } else if (rightPanelTab === 'log' && this.commonDataService.user.hasAccessToTicketLog && isLogVisible) {
                this.ticketRightPanelService.open({ ticket: this.ticket, tabType: TicketTabType.log }, false, false);
            } else if (
                this.commonDataService.user.hasAccessToNotes &&
                isNotesVisible &&
                this.ticketDetailsPanelService.needToOpenNotesTab
            ) {
                this.ticketRightPanelService.open({ ticket: this.ticket, tabType: TicketTabType.notes }, false, false);
            } else if (this.isTicketRightPanelVisible()) {
                this.ticketRightPanelService.open({ ticket: this.ticket }, true);
            }

            const replyId = this.activatedRoute.snapshot.queryParams.replyId;

            if (replyId) {
                this.replyService.scrollToViewReply(replyId, this.ticket);
            }

            setTimeout(() => {
                this.isTicketReady = true;
            }, 500);
        }

        this.ticket.isTicketCanBeReplied = this.replyService.getIsTicketCanBeRepliedStatus(
            this.ticket,
            this.user.id,
            this.isCustomerOnline,
        );
        this.ticket.isReplyCanBeEdited = this.replyService.getIsReplyCanBeEditedStatus(this.ticket, this.user.id);

        this.ticket.selectedRepliesCount = signal(0);
        this.resetSelectedReplies(this.ticket.messageThread as ThreadElementDTO[]);

        if (this.activatedRoute.snapshot.queryParams.action === 'complete') {
            const initialState = {
                ticketId: ticket.id,
                confirmCallback: () => {
                    this.updateStatus(this.ticketStatusEnum.completed);
                },
            };

            this.bsModalService.show(CompleteTicketModalComponent, {
                initialState,
            });
        }

        if (!this.isChat) {
            this.replySortingService.setReplyListSortingType(this.isSocialMediaTicket, this.isFormEmailApi);
        }

        this.loadTagsByCategory(this.ticket.category && this.ticket.category.id);

        setTimeout(() => {
            this.loadedThread = this.loaded = true;
        });
    }

    isTicketRightPanelVisible(): boolean {
        return (
            !this.commonDataService.isOneTime &&
            (this.isCustomerProfileVisible() ||
                this.isNotesTabVisible() ||
                this.isLinkedTicketsTabVisible() ||
                this.isAccessTabVisible() ||
                this.isLogTabVisible() ||
                this.isEditFieldsTabVisible() ||
                this.hasVisibleExternalApps() ||
                this.isFaqVisible())
        );
    }

    private loadTicket(ticketId: number, callback?: () => void): void {
        this.loadedThread = this.loaded = false;
        const previousStatus = this.ticket?.id === ticketId ? this.ticket.status : undefined;

        this.ticketService
            .get(ticketId)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((ticket) => {
                this.fieldsService.ticketCreatorId = ticket.firstMessageCreatorId;
                this.handleUpdatedTicketResult(ticket, previousStatus);

                callback?.();
            });
    }

    formatAuthenticationLevel(level: SessionAuthenticationLevel | number | null | undefined): string {
        switch (Number(level)) {
            case SessionAuthenticationLevel.pinOrBiometrics:
                return `${this.translateService.instant('PIN or biometrics')} (2)`;
            case SessionAuthenticationLevel.bankIdSubstantial:
                return `${this.translateService.instant('BankID Substantial (OTP)')} (8)`;
            case SessionAuthenticationLevel.bankIdHigh:
                return `${this.translateService.instant('BankID High (OTP + password)')} (10)`;
            default:
                return `${this.translateService.instant('Not authenticated')} (0)`;
        }
    }

    requestSessionAuthentication(): void {
        if (!this.ticket?.id || this.isSessionAuthenticationRequestButtonDisabled) {
            return;
        }

        this.isRequestingSessionAuthentication = true;
        this.ticketService
            .requestSessionAuthentication(this.ticket.id)
            .pipe(
                finalize(() => {
                    this.isRequestingSessionAuthentication = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((sessionAuthentication) => {
                this.ticket.sessionAuthentication = sessionAuthentication;
            });
    }

    // todo: move in tags component? and listen there for category update event?
    loadTagsByCategory(id: number): void {
        if (!id) {
            this.tagService.tagsByCategory.length = 0;
            this.tagService.availableTagsForTicket = this.tagService.tagsByCategory.concat(this.tagService.globalTags);
            this.isUpdatingTagsList = false;
        } else {
            this.tagService
                .getTagsByCategory(id)
                .pipe(
                    finalize(() => {
                        this.isUpdatingTagsList = false;
                    }),
                    takeUntil(this.unsubscriber),
                )
                .subscribe((tags) => {
                    tags.forEach((tag: Tag) => {
                        tag['categoryName'] = this.ticket.category.name || this.ticket.category.label;
                    });

                    this.tagService.tagsByCategory = tags;

                    this.tagService.availableTagsForTicket = this.tagService.tagsByCategory.concat(
                        this.tagService.globalTags,
                    );
                });
        }
    }

    private processTicketChanged(newTicketId: number): void {
        this.signalRService.selectedTicketChanged(newTicketId).pipe(take(1)).subscribe();
    }

    isTicketHeaderVisible(key: keyof NonNullable<InboxElementsVisibility['ticketHeader']>): boolean {
        return isInboxElementVisible(this.getInboxElementsVisibility()?.ticketHeader?.[key]);
    }

    isOpenTicketInNewWindowVisible(): boolean {
        return isInboxElementVisible(this.getInboxElementsVisibility()?.openTicketInNewWindow);
    }

    private isCustomerProfileVisible(): boolean {
        return (
            !this.commonDataService.user?.isReadOnly &&
            isInboxElementVisible(this.getInboxElementsVisibility()?.rightPanel?.customerProfile)
        );
    }

    private isNotesTabVisible(): boolean {
        return (
            (!this.commonDataService.user?.isReadOnly || this.commonDataService.user?.hasAccessToNotes === true) &&
            isInboxElementVisible(this.getInboxElementsVisibility()?.rightPanel?.notes)
        );
    }

    private isLinkedTicketsTabVisible(): boolean {
        return (
            !this.commonDataService.user?.isReadOnly &&
            isInboxElementVisible(this.getInboxElementsVisibility()?.rightPanel?.linkedTicketView)
        );
    }

    private isAccessTabVisible(): boolean {
        return (
            !this.commonDataService.user?.isReadOnly &&
            this.ticket?.source !== TicketSource.chat &&
            isInboxElementVisible(this.getInboxElementsVisibility()?.rightPanel?.access)
        );
    }

    private isLogTabVisible(): boolean {
        return (
            (!this.commonDataService.user?.isReadOnly || this.commonDataService.user?.hasAccessToTicketLog === true) &&
            isInboxElementVisible(this.getInboxElementsVisibility()?.rightPanel?.log)
        );
    }

    private isEditFieldsTabVisible(): boolean {
        return isInboxElementVisible(this.getInboxElementsVisibility()?.rightPanel?.editFields);
    }

    private hasVisibleExternalApps(): boolean {
        return Boolean(
            (this.ticket?.source === TicketSource.form || this.ticket?.source === TicketSource.email) &&
                this.ticket?.firstMessageCreator?.email &&
                this.commonDataService.apps?.some((app) => app.isActive),
        );
    }

    private isFaqVisible(): boolean {
        return (
            this.commonDataService.customerSettings?.language === LocalizationLanguage.norwegian ||
            this.commonDataService.user?.localizationLanguage === LocalizationLanguage.norwegian
        );
    }

    private getInboxElementsVisibility(): InboxElementsVisibility | undefined {
        return this.commonDataService.customerSettings?.inboxElementsVisibility;
    }

    private addReplyFromOnetimeLinkConfirmation(): Subject<boolean> {
        const initialState = {
            title: this.translateService.instant('Please confirm that you want to send this reply to the customer'),
            confirmText: this.translateService.instant('Yes, send my reply now'),
            declineText: this.translateService.instant('No, let me revise my reply first'),
        };

        const modalRef = this.bsModalService.show(ConfirmationModalComponent, {
            initialState,
        });

        return modalRef.content.onClose;
    }

    private addUpdateEmailsConfirmation(
        ccUsers: string[],
        toUsers: string[],
        bccUsers: string[],
        isCcFieldNotUpdated: boolean,
        isToFieldNotUpdated: boolean,
        isBccFieldNotUpdated: boolean,
    ): Subject<boolean> {
        const initialState = {
            toUsers: !isToFieldNotUpdated ? toUsers : [],
            ccUsers: !isCcFieldNotUpdated ? ccUsers : [],
            bccUsers: !isBccFieldNotUpdated ? bccUsers : [],
        };
        const modalRef = this.bsModalService.show(RecipientConfirmationModalComponent, {
            initialState,
        });

        return modalRef.content.onClose;
    }

    private isLatestPageUrlVisible(): boolean {
        const latestPageUrl = document.getElementById('latest-page-url');
        if (!latestPageUrl || !this.ticket.latestPageUrl) {
            return false;
        }
        const scrollContainer = document.getElementById('scroll-container');
        if (!scrollContainer) {
            return false;
        }
        const elementRectangle = latestPageUrl.getBoundingClientRect();
        const containerRectangle = scrollContainer.getBoundingClientRect();
        const middleOfElement = elementRectangle.top + elementRectangle.height / 2;
        return middleOfElement < containerRectangle.bottom && middleOfElement > containerRectangle.top;
    }

    private logToCrmConfirmation(): Subject<SendTicketToSORequest> {
        const initialState = {
            ticketTitle: this.ticket.title,
            ticket: this.ticket,
        };

        const modalRef = this.bsModalService.show(SuperOfficeIntegrationModalComponent, {
            initialState,
            ignoreBackdropClick: true,
        });

        return modalRef.content.onClose;
    }

    private updateAuthorNameInThread(
        elements: Array<ThreadElementDTO | SatisfactionDTO | SurveyDTO>,
        userId: string,
        name: string,
    ): void {
        elements.forEach((element) => {
            if ('author' in element && element.author?.id === userId) {
                element.author.name = name;
            }
            if ('subReplies' in element && element.subReplies?.length) {
                this.updateAuthorNameInThread(element.subReplies, userId, name);
            }
        });
    }

    private processDeletedDraft(replyId: number, parentReply?: ThreadElementDTO): void {
        if (parentReply) {
            parentReply.subReplies = parentReply.subReplies.filter((subReply) => {
                return subReply.id !== replyId;
            });
        } else {
            this.ticket.messageThread = this.ticket.messageThread.filter((reply) => {
                return reply.id !== replyId && reply.type === MessageThreadElementType.reply;
            });
        }

        this.threadFilterService.refreshUpdateStamp();
    }

    processTicketStatus(newStatus: TicketStatus): void {
        this.ticket.status = newStatus;
        if (newStatus === this.ticketStatusEnum.completed) {
            this.notificationsService.success(
                this.translateService.instant('Success!'),
                this.translateService.instant('The ticked was marked as "Closed"'),
            );
        }

        this.ticket.isTicketCanBeReplied = this.replyService.getIsTicketCanBeRepliedStatus(
            this.ticket,
            this.user.id,
            this.isCustomerOnline,
        );
        this.ticket.isReplyCanBeEdited = this.replyService.getIsReplyCanBeEditedStatus(this.ticket, this.user.id);
    }

    handleTicketStatusUpdated({
        status,
        previousStatus,
    }: {
        status: TicketStatus;
        previousStatus: TicketStatus;
    }): void {
        this.applyReturnedStatus(status, previousStatus);
    }

    handleUpdatedTicketFromAction(ticket: DetailedTicketDTO): void {
        this.handleUpdatedTicketResult(ticket, this.ticket?.status);
    }

    private handleTicketClosedAfterUpdate(): void {
        this.openTicketInNewWindowService.closeTicketWindow(this.ticket.id);

        if (this.isSingleTicketView) {
            this.ticketDetailsPanelService.setExplicitEmptySelection(this.ticket.id);
            window.close();
            return;
        }

        if (this.commonDataService.customerSettings.isClosedTicketRemovedFromView !== true) {
            return;
        }

        this.ticketDetailsPanelService
            .setExplicitEmptySelection(
                this.ticket.id,
                this.commonDataService.customerSettings.isNextTicketAutomaticallyOpened === true,
            )
            .then((navigated) => {
                if (navigated) {
                    this.componentMessengerService.needToReload();
                }
            });
    }

    private clearInvalidInitialSelection(): void {
        if (this.isSingleTicketView) {
            return;
        }

        this.ticketDetailsPanelService.clearRouteSelection(this.ticket.id);
        this.componentMessengerService.needToReload();
    }

    private applyReturnedStatus(status: TicketStatus, previousStatus = this.ticket.status): void {
        const wasClosedByThisFlow = previousStatus !== TicketStatus.completed && status === TicketStatus.completed;

        if (this.ticket.status === status && !wasClosedByThisFlow) {
            return;
        }

        if (this.ticket.status !== status) {
            this.processTicketStatus(status);
        }

        if (status === TicketStatus.completed) {
            this.handleTicketClosedAfterUpdate();
        }
    }

    private handleUpdatedTicketResult(ticket: DetailedTicketDTO, previousStatus: TicketStatus | undefined): void {
        this.processLoadTicket(ticket);

        if (
            previousStatus === undefined &&
            this.commonDataService.customerSettings.isClosedTicketRemovedFromView === true &&
            !this.doesTicketMatchCurrentStatusFilter(ticket.status)
        ) {
            this.clearInvalidInitialSelection();
            return;
        }

        if (previousStatus !== undefined) {
            this.applyReturnedStatus(ticket.status, previousStatus);
        }
    }

    private processUpdatedReply(updatedReply: ThreadElementDTO, isDeleting = false): void {
        this.replyService.updateThreadLastUpdateTime(
            this.ticket.messageThread,
            updatedReply.threadLastUpdateTime,
            updatedReply.parentReplyIds,
        );

        this.replyService.updateReplyData(this.ticket, updatedReply, this.socialMediaSourceId, isDeleting);

        this.threadFilterService.refreshUpdateStamp();

        this.ticket.isReplyCanBeEdited = this.replyService.getIsReplyCanBeEditedStatus(this.ticket, this.user.id);
    }

    private resetSelectedReplies(replies: ThreadElementDTO[]): void {
        replies.forEach((reply: ThreadElementDTO) => {
            reply.isSelected = false;
            if (reply.subReplies) {
                this.resetSelectedReplies(reply.subReplies);
            }
        });
    }

    // todo: move to separated service?
    private setTicketViewType(): void {
        switch (this.ticket.source) {
            case TicketSource.form:
            case TicketSource.email:
            case TicketSource.api: {
                this.ticketViewType = TicketView.formEmailApi;
                break;
            }
            case TicketSource.chat:
            case TicketSource.whatsApp: {
                this.ticketViewType = TicketView.chat;
                break;
            }
            default: {
                if (
                    [DetailedTicketSource.facebookPrivateMessage, DetailedTicketSource.instagramDirectMessage].includes(
                        this.ticket.detailedSource,
                    )
                ) {
                    this.ticketViewType = TicketView.chat;
                } else {
                    this.ticketViewType = TicketView.socialMedia;
                }

                this.isSocialMediaTicket = true;
            }
        }
    }

    private setupSignalRListeners(): void {
        this.signalRService
            .onConnected()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(() => {
                this.signalRService.selectedTicketChanged(this.ticket.id).pipe(take(1)).subscribe();
            });

        this.signalRService
            .onTicketUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: TicketUpdatedEventModel) => {
                this.updateTicket(data.ticket.id, data.ticket);
            });
        this.signalRService
            .onUserUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(({ userId, name }) => {
                if (this.ticket?.firstMessageCreator?.id === userId) {
                    this.ticket.firstMessageCreator.name = name;
                }

                if (this.ticket?.messageThread) {
                    this.updateAuthorNameInThread(this.ticket.messageThread, userId, name);
                }
            });
        this.signalRService
            .onTicketPropertiesUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: TicketPropertiesUpdatedEventModel) => {
                if (data.ticketId !== this.ticket?.id) {
                    return;
                }

                const isLatestPageUrlChanged =
                    data.ticketProperties.latestPageUrl != null &&
                    this.ticket.latestPageUrl !== data.ticketProperties.latestPageUrl;

                if (isLatestPageUrlChanged && !this.isLatestPageUrlVisible()) {
                    this.notificationsService.info(
                        this.translateService.instant('Socialboards Chat'),
                        this.translateService.instant('Latest page url was updated'),
                    );
                }
                if (isLatestPageUrlChanged) {
                    this.ticket.latestPageUrlUpdatedAt = data.ticketProperties.latestPageUrlUpdatedAt ?? null;
                }
                this.updateTicket(data.ticketId, data.ticketProperties);
            });

        this.signalRService
            .onTicketAssigneeUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: TicketAssigneeUpdatedEventModel) => {
                if (this.ticket?.id === data.ticketId) {
                    if (!this.commonDataService.isOneTime) {
                        this.assignService.showAlertOnTicketAssigned(this.ticket, data.ticketAssignee);
                    }

                    const isAssignmentAlreadyApplied =
                        this.ticket.isAssigned === data.ticketAssignee.isAssigned &&
                        this.ticket.assignee?.id === data.ticketAssignee.assignee?.id &&
                        this.ticket.assignerId === data.ticketAssignee.assignerId &&
                        this.ticket.isAssignedToBot === data.ticketAssignee.isAssignedToBot;

                    if (isAssignmentAlreadyApplied) {
                        return;
                    }

                    Object.assign(this.ticket, data.ticketAssignee);
                    this.ticketUpdateStamp = new Date().getTime();
                }
            });

        this.signalRService
            .onTicketThreadUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: TicketThreadUpdatedEventModel) => {
                if (this.ticket?.id === data.ticketId) {
                    if (data?.operationId === this.completedOperationId) {
                        return;
                    }

                    if (!this.ticketService.isRedacting) {
                        this.completedOperationId = data?.operationId;
                        this.loadTicket(this.ticket.id);
                    }
                }
            });

        this.signalRService
            .onReplyAdded()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: ReplyAddedEventModel) => {
                if (this.loaded && data.reply.ticketId === this.ticket.id) {
                    if (this.ticketViewType === TicketView.chat && this.ticket.source !== TicketSource.chat) {
                        this.componentMessengerService.addNewReply(data.reply);
                    }

                    this.replyService.processNewReply(this.ticket, data.reply);

                    this.replyService.updateThreadLastUpdateTime(
                        this.ticket.messageThread,
                        data.reply.threadLastUpdateTime,
                        data.reply.parentReplyIds,
                    );

                    this.threadFilterService.refreshUpdateStamp();

                    if (data.reply.author?.id === this.commonDataService.user.id) {
                        this.replyStoreService.clearTempReply(data.reply.parentReplyId, data.reply.ticketId);
                    }
                }
            });

        this.signalRService
            .onReplySuggestionsUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: ReplySuggestionsUpdatedEventModel) => {
                if (this.ticket && this.ticket.id === data.ticketId) {
                    this.ticket.suggestions = data.suggestions;
                }
            });

        this.signalRService
            .onReplyUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: ReplyUpdatedEventModel) => {
                if (data.ticketId === this.ticket.id) {
                    this.processUpdatedReply(data.reply);
                }
            });

        this.signalRService
            .onReplyDeleted()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: ReplyDeletedEventModel) => {
                if (data.ticketId === this.ticket.id) {
                    this.processUpdatedReply(data.reply, true);
                }
            });

        this.signalRService
            .onReplyDraftDeleted()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: ReplyDeletedEventModel) => {
                if (data.ticketId === this.ticket.id) {
                    let parentReply;
                    if (data.parentReplyId) {
                        parentReply = this.replyService.findReply(this.ticket.messageThread, data.parentReplyId);
                    }
                    this.processDeletedDraft(data.replyId, parentReply);
                }
            });

        this.signalRService
            .onNoteCountUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: NoteCountUpdatedEventModel) => {
                if (data.ticketId === this.ticket.id) {
                    this.ticket.noteCount = data.noteCount;
                    this.ticket.unreadNoteCount = data.unreadNoteCount;
                }
            });
    }

    private updateTicket(ticketId: number, ticket: Partial<TicketSignalRDTO>): void {
        if (ticketId === this.ticket?.id) {
            const previousStatus = this.ticket.status;

            if (this.commonDataService.user.role === UserRole.vendor) {
                ticket.isAssigned = false;
                ticket.assignee = null;
            }

            // SignalR partial updates can send null for unchanged nullable fields; keep the current auth state.
            const ticketUpdate =
                ticket.sessionAuthentication === null && this.ticket.sessionAuthentication
                    ? { ...ticket, sessionAuthentication: this.ticket.sessionAuthentication }
                    : ticket;

            Object.assign(this.ticket, ticketUpdate);

            this.replyService.processAttachments(this.ticket);
            this.ticket.messageThread.forEach((message) => {
                this.replyService.processAttachments(message);
            });

            this.ticket.isTicketCanBeReplied = this.replyService.getIsTicketCanBeRepliedStatus(
                this.ticket,
                this.user.id,
                this.isCustomerOnline,
            );
            this.ticket.isReplyCanBeEdited = this.replyService.getIsReplyCanBeEditedStatus(this.ticket, this.user.id);
            this.ticketUpdateStamp = new Date().getTime();

            if (ticket.status !== undefined) {
                this.applyReturnedStatus(this.ticket.status, previousStatus);
            }
        }
    }

    private doesTicketMatchCurrentStatusFilter(ticketStatus: TicketStatus): boolean {
        const currentStatus = +(this.activatedRoute.snapshot.queryParams.status ?? TicketStatus.all);

        switch (currentStatus) {
            case TicketStatus.open:
            case TicketStatus.pending:
            case TicketStatus.completed:
                return ticketStatus === currentStatus;
            case TicketStatus.notCompleted:
                return ticketStatus !== TicketStatus.completed;
            default:
                return true;
        }
    }

    ngOnDestroy(): void {
        this.noteService.isEditorForNewNoteShown = false;

        this.noteService.referencedReply = null;

        this.quillHelperService.editedReplyId = null;

        this.ticketDetailsPanelService.isTicketDetailsOpened = false;

        if (this.rightPanelService.isRightPanelOpened) {
            this.rightPanelService.close();
        }

        if (this.ticketRightPanelService.isRightPanelOpened) {
            this.ticketRightPanelService.close(true);
        }

        this.ticketDetailsPanelService.close(this.ticket.id);

        this.assignService.isAssigningTicket = false;
        this.assignService.isShowAssigningSelect = false;

        document.removeEventListener('keyup', this.handleKeyUpClick);
        document.removeEventListener('keydown', this.handleKeyDownClick);

        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
